import { Component, OnInit, Input, SimpleChanges, OnChanges, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { ITypesSelection } from 'src/app/core/interfaces/types-selection.interface';
import { TexteService } from 'src/app/core/services/texte.service';
import { FormGroup, FormControl, Validators, ValidatorFn, ValidationErrors, AbstractControl } from '@angular/forms';
import { IPatient } from 'src/app/core/interfaces/patient.interface';
import { ApiService, PaysDto } from 'src/app/core/services/api.service';
import { ToolboxService } from 'src/app/core/services/toolbox.service';

@Component({
    selector: 'app-patient-form',
    templateUrl: './patient-form.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PatientFormComponent implements OnInit, OnChanges {

  @Input() typesSelected: ITypesSelection;
  @Output() indicatifs: EventEmitter<number[]> = new EventEmitter<number[]>();

  public patientForm: FormGroup;
  public pays: PaysDto[];

  constructor(
    public texteService: TexteService,
    private apiService: ApiService,
    private toolboxService: ToolboxService
  ) { }

  ngOnInit(): void {
    this.pays = [];
    this.buildForm();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.typesSelected != null && !changes.typesSelected.firstChange) {
      this.buildForm();
    }
  }

  private buildForm() {
    this.patientForm = new FormGroup({
      nom: new FormControl('', Validators.required),
      nom2: new FormControl(''),
      prenom: new FormControl('', Validators.required),
      sexe: new FormControl('', Validators.required),
      dateNaissance: new FormControl('', Validators.required),
    });

    if (this.typesSelected.situation.code === 'FRA' && this.typesSelected.demande == null) {
      // Autre
      this.patientForm.addControl('dossier', new FormControl('', [Validators.required, this.numDossierValidator()]));
    } else if (this.typesSelected.situation.code === 'MED') {
      // Médecin
      this.patientForm.addControl('telephone', new FormControl('', [Validators.required, this.toolboxService.telValidator()]));
      this.patientForm.addControl('telephone2', new FormControl('', this.toolboxService.telValidator()));
      this.patientForm.addControl('email', new FormControl('', Validators.email));
    } else if (this.typesSelected.situation.code === 'INT') {
      // Internationnal
      this.apiService.getPays().subscribe(data => {
        data.forEach(pays => {
          pays.libelle = this.texteService.getTexte(pays.codeTexte, pays.libelle);
        });        
        data.sort((a,b) => {
          return (a.libelle > b.libelle) ? 1 : ((b.libelle > a.libelle) ? -1 : 0);
        });

        this.pays = data;
      });

      this.patientForm.addControl('adresse', new FormControl('', Validators.required));
      this.patientForm.addControl('ville', new FormControl('', Validators.required));
      this.patientForm.addControl('pays', new FormControl('', Validators.required));
      this.patientForm.addControl('nationnalite', new FormControl('', Validators.required));
      this.patientForm.addControl('visa', new FormControl('', Validators.required));
    }
  }

  numDossierValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (this.toolboxService.stringIsNullOrWhiteSpace(control.value.toString())) {
        return null;
      }
      const regexp = new RegExp(/^\d{9}[a-zA-Z]{2}$/g);
      const match = regexp.test(control.value);

      return match ? null : { dossierUnvalid: true };
    };
  }

  public onPaysChange() {
    if (this.patientForm.get('pays').value != null) {
      this.indicatifs.emit(this.patientForm.get('pays').value.indicatifs);
    }
  }

  public isValid(): boolean {
    return this.patientForm.valid;
  }

  public getForm(): IPatient {
    const date = this.patientForm.get('dateNaissance').value as Date;
    date.setUTCDate((this.patientForm.get('dateNaissance').value as Date).getDate());

    return {
      dossier: this.typesSelected.situation.code === 'FRA' && this.typesSelected.demande == null ?
        this.patientForm.get('dossier').value : null,
      nom: this.patientForm.get('nom').value,
      nom2: this.patientForm.get('nom2').value,
      prenom: this.patientForm.get('prenom').value,
      sexe: this.patientForm.get('sexe').value,
      dateNaissance: date,
      telephone: this.typesSelected.situation.code === 'MED' ? this.patientForm.get('telephone').value : null,
      telephone2: this.typesSelected.situation.code === 'MED' ? this.patientForm.get('telephone2').value : null,
      email: this.typesSelected.situation.code === 'MED' ? this.patientForm.get('email').value : null,
      adresse: this.typesSelected.situation.code === 'INT' ? this.patientForm.get('adresse').value : null,
      ville: this.typesSelected.situation.code === 'INT' ? this.patientForm.get('ville').value : null,
      pays: this.typesSelected.situation.code === 'INT' ? this.patientForm.get('pays').value : null,
      nationnalite: this.typesSelected.situation.code === 'INT' ? this.patientForm.get('nationnalite').value : null,
      visa: this.typesSelected.situation.code === 'INT' ? this.patientForm.get('visa').value : null,
    } as IPatient;
  }
}
